import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'sonner';
import { Mail, MapPin, MessageSquare, Phone, Send } from 'lucide-react';
import { api } from '@/lib/api/client';
import { contactDetails, importedSiteImages } from '@/content/public-site-content';

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  recipient: z.enum(['dominion_mandate', 'dr_david']),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

type ContactFormValues = z.infer<typeof contactSchema>;

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      recipient: 'dr_david',
      message: '',
    },
  });

  async function onSubmit(values: ContactFormValues) {
    const response = await api.post('/api/contact', values);
    if (response.success) {
      toast.success('Message submitted successfully. I will respond to you as soon as possible.');
      setSubmitted(true);
      return;
    }

    toast.error('An error occurred. Please try again later.');
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <section className="overflow-hidden rounded-[2rem] border border-border/70 bg-card shadow-sm">
        <div className="grid gap-0 lg:grid-cols-[1fr_0.9fr]">
          <div className="space-y-5 p-8 sm:p-10">
            <Badge variant="outline" className="w-fit border-primary/30 bg-primary/5">
              <MessageSquare className="mr-1 h-3 w-3 text-primary" />
              Contact
            </Badge>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Contact</h1>
            <div className="space-y-3 text-muted-foreground">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-primary" />
                <a href={`mailto:${contactDetails.primary.email}`} className="hover:text-foreground">
                  {contactDetails.primary.email}
                </a>
              </div>
              {contactDetails.primary.phones.map((phone) => (
                <div key={phone} className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-primary" />
                  <a href={`tel:${phone}`} className="hover:text-foreground">
                    {phone}
                  </a>
                </div>
              ))}
            </div>
          </div>
          <div className="relative min-h-[280px] bg-muted">
            <img src={importedSiteImages.contactHero} alt="Contact Dr David" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="grid gap-4 sm:grid-cols-2">
          {contactDetails.offices.map((office) => (
            <Card key={office.name} className="border-border/70 bg-card/90">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPin className="h-4 w-4 text-primary" />
                  {office.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1 text-sm text-muted-foreground">
                  {office.address.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
                <div className="space-y-2 border-t border-border/60 pt-3 text-sm">
                  {office.phones.map((phone) => (
                    <a key={phone} href={`tel:${phone}`} className="block text-muted-foreground hover:text-foreground">
                      {phone}
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle>Contact Form</CardTitle>
            <CardDescription>Full name, email, and your message.</CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="space-y-4 py-10 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <Send className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Notification!</h3>
                  <p className="text-sm text-muted-foreground">
                    Message submitted successfully. I will respond to you as soon as possible.
                  </p>
                </div>
                <Button variant="outline" onClick={() => { setSubmitted(false); form.reset(); }}>
                  Send another message
                </Button>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full name</FormLabel>
                        <FormControl>
                          <Input placeholder="Full name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="Email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Message</FormLabel>
                        <FormControl>
                          <Textarea rows={6} placeholder="Type message here" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full">
                    <Send className="mr-2 h-4 w-4" />
                    Send Message
                  </Button>
                </form>
              </Form>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
